import { inject } from '@angular/core';
import { RedirectCommand, ResolveFn, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Option } from '@sapphire/result';
import { map, of } from 'rxjs';

import { AuthSelectorsFacade } from '@ps/auth-data-access';
import { APP_ROUTES } from '@ps/core-config';
import { isUnimpersonatedOperator, Survey } from '@ps/survey-utils';

import { SurveyCreationActionsFacade } from '../state/survey-creation.actions.facade';
import { SurveyChildKey } from '../state/survey-creation-shared-state.model';
import { parseSurveyId } from './domain/survey-load-outcome';
import { surveyRedirectRoute } from './utils/survey-redirect-route';
import { awaitSurveyLoad } from './utils/survey-load';
import { navigationActiveChildKey } from './utils/navigation-active-child-key';

interface SurveyPage {
  /** Starts the page's own part of the survey load, refetching a survey already in memory. */
  readonly initialize: (
    actions: SurveyCreationActionsFacade,
    surveyId: number,
    activeChildKey: Option<SurveyChildKey>,
  ) => void;
  /** This page's route for a survey id. */
  readonly routeFor: (surveyId: number) => string;
}

const EDIT_SURVEY_PAGE: SurveyPage = {
  initialize: (actions, surveyId, activeChildKey) =>
    actions.initAudience(surveyId, activeChildKey, true),
  routeFor: APP_ROUTES.editSurvey,
};

// The launch page never shows feasibility, score or supplier data.
const LAUNCH_SURVEY_PAGE: SurveyPage = {
  initialize: (actions, surveyId, activeChildKey) =>
    actions.initLaunch(surveyId, activeChildKey, true),
  routeFor: APP_ROUTES.launchSurvey,
};

/**
 * Loads the survey named by the route's `:id` into the survey-creation state before the page renders,
 * then checks that the page is the right place for it.
 * Gate only: the survey is read from the store, never from route data.
 *
 * A bad id or a failed load goes to Not Found; `surveyRedirectRoute` checks where a survey that does not belong on this page goes.
 * This is the page's only `init`: it always refetches, and carries over the child the navigation
 * named (`navigationActiveChildKey`). The state is cleared only when the workflow is left
 * (`SurveyWorkflowLayoutComponent`), so a move between workflow pages keeps what this loaded.
 */
function resolveSurveyFor(page: SurveyPage): ResolveFn<boolean> {
  return (route) => {
    const router = inject(Router);
    const store = inject(Store);
    const actions = inject(SurveyCreationActionsFacade);
    const auth = inject(AuthSelectorsFacade);

    const redirectTo = (url: string): RedirectCommand =>
      new RedirectCommand(router.parseUrl(url));

    const checkPage = (
      routeSurveyId: number,
      survey: Survey,
    ): boolean | RedirectCommand =>
      surveyRedirectRoute({
        routeSurveyId,
        survey,
        isOperatorWithoutBuyer: isUnimpersonatedOperator(
          auth.userType(),
          auth.hasImpersonatedBuyer(),
        ),
        routeFor: page.routeFor,
      }).match({ some: redirectTo, none: () => true });

    return parseSurveyId(route.paramMap.get('id')).match({
      err: () => of(redirectTo(APP_ROUTES.NOT_FOUND)),
      ok: (surveyId) => {
        page.initialize(actions, surveyId, navigationActiveChildKey(router));
        return awaitSurveyLoad(store, surveyId).pipe(
          map((outcome) =>
            outcome.match({
              ok: (survey) => checkPage(surveyId, survey),
              err: () => redirectTo(APP_ROUTES.NOT_FOUND),
            }),
          ),
        );
      },
    });
  };
}

export const surveyResolver = resolveSurveyFor(EDIT_SURVEY_PAGE);

export const launchSurveyResolver = resolveSurveyFor(LAUNCH_SURVEY_PAGE);
